import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, BookOpen, Sparkles, Landmark, Search, Volume2, VolumeX } from "lucide-react";
import { IMAGES, FALLBACK_IMAGE } from "../lib/images";
import { ORISA } from "../lib/orisa";
import { Reveal } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import YorubaFinder from "../components/site/YorubaFinder";
import WordOfDay from "../components/site/WordOfDay";
import ProverbOfDay from "../components/site/ProverbOfDay";
import RoyalTitleOfDay from "../components/site/RoyalTitleOfDay";
import NameOfDay from "../components/site/NameOfDay";
import QuizOfDay from "../components/site/QuizOfDay";
import QuestionOfDay from "../components/site/QuestionOfDay";
import RoyalReel from "../components/site/RoyalReel";
import FeaturedProducts from "../components/site/FeaturedProducts";
import api from "../lib/api";

const FEATURES = [
  {
    title: "Beginner Guide to Yorùbá Culture",
    desc: "New here? Start with the essentials — who the Yorùbá are, where they come from, and why their story matters.",
    to: "/topic/origins-and-history",
    icon: Compass,
    image: IMAGES.storytellingPortrait,
  },
  {
    title: "Yorùbá Philosophy",
    desc: "Explore Omolúàbí, Orí, and a worldview where character, destiny, and Àṣẹ shape a life well lived.",
    to: "/topic/worldview-philosophy",
    icon: BookOpen,
    image: IMAGES.manScarification,
  },
  {
    title: "Òrìṣà & Spirituality",
    desc: "Meet the divine forces of the Yorùbá universe — Ṣàngó, Ọ̀ṣun, Ògún — and the wisdom of Ifá.",
    to: "/orisa",
    icon: Sparkles,
    image: IMAGES.woodCarving,
  },
  {
    title: "History & Origins",
    desc: "Journey from the sacred city of Ilé-Ifẹ̀ through the great empire of Ọ̀yọ́ to the global diaspora.",
    to: "/timeline",
    icon: Landmark,
    image: IMAGES.beadedCrownWoman,
  },
];

export default function Home() {
  const [articles, setArticles] = React.useState([]);
  const [heroImage, setHeroImage] = React.useState(IMAGES.heroMain);
  const [whoImage, setWhoImage] = React.useState(IMAGES.womanBeaded);
  const [featuredAd, setFeaturedAd] = React.useState(null);
  const bannerVideoRef = React.useRef(null);
  const [bannerMuted, setBannerMuted] = React.useState(true);
  React.useEffect(() => {
    api.get("/articles?limit=3").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => {
      if (r.data?.hero_image) setHeroImage(r.data.hero_image);
      if (r.data?.who_image) setWhoImage(r.data.who_image);
    }).catch(() => {});
    api.get("/adverts/featured").then((r) => setFeaturedAd(r.data || null)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="ÀsÀNigHT Worldwide in traditional Yorùbá attire"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/55 to-night/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pb-20 lg:pb-28 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-6"
          >
            Preserving Heritage · Sharing Knowledge · Inspiring Identity
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[1.02] max-w-4xl text-shadow-hero"
          >
            Discover the Depth of Yorùbá Culture
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-7 text-lg md:text-xl text-cream/80 max-w-2xl leading-relaxed"
          >
            ÀsÀNigHT Worldwide brings Yorùbá traditions, heritage, and knowledge to a global
            audience. Explore, learn, and celebrate with educational content rooted in
            authenticity.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/topic/origins-and-history"
              className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-8 py-4 rounded-full hover:bg-gold-light transition-colors"
              data-testid="hero-start-learning-button"
            >
              Start Learning <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/orisa"
              className="inline-flex items-center gap-2 border border-cream/40 text-cream font-semibold px-8 py-4 rounded-full hover:bg-cream hover:text-night transition-colors"
              data-testid="hero-explore-culture-button"
            >
              Explore Culture
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED ADVERT BANNER */}
      {featuredAd && (
        <section className="bg-night" data-testid="home-featured-advert">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="relative rounded-3xl overflow-hidden border border-gold/20 grain-overlay min-h-[300px] md:min-h-[380px] flex items-center">
              <div className="absolute inset-0">
                {featuredAd.media_type === "video" ? (
                  <video ref={bannerVideoRef} src={featuredAd.media_url} poster={featuredAd.poster || undefined} muted loop autoPlay playsInline className="w-full h-full object-cover" />
                ) : (
                  <img src={featuredAd.media_url} alt={featuredAd.title || "Featured advert"} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-night via-night/80 to-night/30" />
              </div>
              {featuredAd.media_type === "video" && (
                <button
                  onClick={() => { const v = bannerVideoRef.current; if (!v) return; const n = !bannerMuted; setBannerMuted(n); v.muted = n; if (!n) v.play().catch(() => {}); }}
                  data-testid="home-featured-advert-sound"
                  title={bannerMuted ? "Unmute" : "Mute"}
                  aria-label={bannerMuted ? "Unmute video" : "Mute video"}
                  className="absolute bottom-4 right-4 z-10 grid place-items-center w-11 h-11 rounded-full bg-night/70 text-cream hover:bg-gold hover:text-night backdrop-blur transition-colors"
                >
                  {bannerMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              )}
              <div className="relative max-w-xl px-8 md:px-12 py-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-gold text-xs font-semibold px-3 py-1 mb-4 uppercase tracking-widest">Featured</span>
                {featuredAd.title && <h2 className="font-serif text-3xl md:text-4xl text-cream leading-tight mb-3">{featuredAd.title}</h2>}
                {featuredAd.description && <p className="text-cream/75 text-base md:text-lg leading-relaxed mb-6">{featuredAd.description}</p>}
                {featuredAd.link_url && (
                  <a
                    href={featuredAd.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { api.post(`/adverts/${featuredAd.id}/click`).catch(() => {}); }}
                    data-testid="home-featured-advert-link"
                    className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-7 py-3.5 rounded-full hover:bg-gold-light transition-colors"
                  >
                    {featuredAd.link_label || "Learn More"} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MISSION STRIP */}
      <section className="bg-sand border-b border-parchment">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-ink leading-snug">
              A living civilisation of over{" "}
              <span className="text-forest">45 million people</span> — its philosophy, art,
              and spirituality woven into the fabric of world culture, from{" "}
              <span className="italic">Ilé-Ifẹ̀</span> to the global diaspora.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED SECTIONS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">Where to Begin</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink max-w-2xl mb-14">
            Four doorways into a rich and ancient world
          </h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <Link
                to={f.to}
                data-testid={`feature-card-${f.to}`}
                className="group block h-full border border-parchment rounded-xl overflow-hidden bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-night/70 to-transparent" />
                  <span className="absolute top-4 left-4 grid place-items-center w-11 h-11 rounded-full bg-cream/90 text-forest">
                    <f.icon className="w-5 h-5" />
                  </span>
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl text-ink mb-3 group-hover:text-forest transition-colors">
                    {f.title}
                  </h3>
                  <p className="text-ink-muted leading-relaxed mb-5">{f.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gold">
                    Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDITORIAL SPLIT — WHO ARE THE YORUBA */}
      <section className="bg-forest text-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5 lg:col-start-1">
            <div className="relative">
              <img
                src={whoImage}
                alt="Yorùbá woman in traditional attire"
                className="rounded-xl w-full object-cover aspect-[4/5]"
              />
              <div className="absolute -bottom-6 -right-6 hidden md:block w-40 h-40 border border-gold/40 rounded-xl -z-0" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-4">
              The Learning Hub
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              Who Are the Yorùbá?
            </h2>
            <p className="text-cream/75 text-lg leading-relaxed mb-8">
              From origins and language to religion, social values, art, and global influence
              — our structured learning hub guides you through every dimension of Yorùbá life,
              designed to be beginner-friendly yet deeply informative.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["Origins & History", "/topic/origins-and-history"],
                ["The Yorùbá Language", "/topic/language"],
                ["Religion & Spirituality", "/topic/religion-and-spirituality"],
                ["Arts & Creativity", "/topic/arts-culture-creativity"],
              ].map(([t, to]) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center justify-between border border-cream/20 rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/5 transition-colors"
                  data-testid={`hub-link-${to}`}
                >
                  <span className="text-sm">{t}</span>
                  <ArrowRight className="w-4 h-4 text-gold" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* YORÙBÁ NAME & WORD FINDER */}
      <WordOfDay />
      <YorubaFinder />

      {/* PROVERB OF THE DAY */}
      <ProverbOfDay />

      {/* ROYAL TITLE OF THE DAY */}
      <RoyalTitleOfDay />

      {/* ROYAL REEL — mini slideshow */}
      <RoyalReel />

      {/* NAME OF THE DAY */}
      <NameOfDay />

      {/* QUIZ OF THE DAY */}
      <QuizOfDay />

      {/* QUESTION OF THE DAY */}
      <QuestionOfDay />

      {/* ORISA PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">The Divine Forces</p>            <h2 className="font-serif text-4xl md:text-5xl text-ink">Meet the Òrìṣà</h2>
          </Reveal>
          <Reveal>
            <Link
              to="/orisa"
              className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-gold transition-colors"
              data-testid="view-encyclopedia-link"
            >
              View full encyclopedia <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ORISA.slice(0, 4).map((o, i) => (
            <Reveal key={o.name} delay={i * 0.06}>
              <Link
                to="/orisa"
                className="group block relative rounded-xl overflow-hidden aspect-[3/4]"
                data-testid={`orisa-preview-${o.name}`}
              >
                <img
                  src={o.image}
                  alt={o.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night via-night/30 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="text-xs uppercase tracking-widest text-gold-soft mb-1">
                    {o.color}
                  </p>
                  <h3 className="font-serif text-2xl text-cream">{o.name}</h3>
                  <p className="text-xs text-cream/70 mt-1">{o.domain}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GLOBAL RELEVANCE BAND */}
      <section className="relative grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.festivalCrowd} alt="A Yorùbá festival procession of devotees in white by a sacred river" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-night/75" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-28 text-center">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-5">
              Where Yorùbá Tradition Meets the World
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-cream leading-tight mb-6">
              A heritage that shapes the modern world
            </h2>
            <p className="text-cream/75 text-lg leading-relaxed mb-9">
              From Afrobeats and global fashion to spiritual traditions across the Americas,
              Yorùbá culture continues to inspire and connect millions. Discover its living
              relevance today.
            </p>
            <Link
              to="/topic/modern-relevance"
              className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-8 py-4 rounded-full hover:bg-gold-light transition-colors"
              data-testid="modern-relevance-cta"
            >
              Explore Modern Relevance <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* JOURNAL PREVIEW */}
      {articles.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <Reveal>
              <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">From the Journal</p>
              <h2 className="font-serif text-4xl md:text-5xl text-ink">Latest Articles</h2>
            </Reveal>
            <Reveal>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-gold transition-colors"
                data-testid="view-journal-link"
              >
                Visit the journal <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <Link
                  to={`/blog/${a.slug}`}
                  className="group block h-full border border-parchment rounded-xl overflow-hidden bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300"
                  data-testid={`journal-card-${a.slug}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={a.cover_image || FALLBACK_IMAGE} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-widest text-gold mb-2">{a.category}</p>
                    <h3 className="font-serif text-xl text-ink leading-snug group-hover:text-forest transition-colors">{a.title}</h3>
                    <p className="text-sm text-ink-muted mt-3 line-clamp-3">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <FeaturedProducts />

      <Newsletter />
    </div>
  );
}
